import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";
import JSZip from "jszip";
import { openPdfOnce } from "../components/common/Casecard/HearingBundles/pdfDocumentCache";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const MAX_CONCURRENT_PAGE_READS = 4;
const PAGE_COUNT_TIMEOUT_MS = 15000;
const pageCountCache = {};
const pendingPageCountReads = {};
const queue = [];
let activeReads = 0;

const getDocumentUrl = (doc) => {
  const url = doc?.url?.url || doc?.url;
  return typeof url === "string" ? url : "";
};

const cleanFileRef = (value = "") => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getDocumentFileRef = (doc) =>
  [
    doc?.name,
    doc?.fileName,
    doc?.filename,
    doc?.documentName,
    doc?.originalName,
    doc?.type,
    doc?.fileType,
    doc?.mimeType,
    doc?.contentType,
    doc?.url?.type,
    doc?.url?.fileType,
    doc?.url?.mimeType,
    doc?.url?.contentType,
    getDocumentUrl(doc),
  ]
    .filter((value) => typeof value === "string")
    .map(cleanFileRef)
    .join(" ")
    .toLowerCase();

const hasFileExtension = (value, extension) => {
  const cleanValue = cleanFileRef(value || "")
    .toLowerCase()
    .split("?")[0]
    .split("#")[0];

  return cleanValue.endsWith(extension);
};

const isPdfUrl = (url) => hasFileExtension(url, ".pdf");
const isDocxUrl = (url) => hasFileExtension(url, ".docx");
const isDocUrl = (url) => hasFileExtension(url, ".doc");
const isPdfDocument = (doc) => getDocumentFileRef(doc).includes(".pdf");
export const isWordDocument = (doc) => {
  const fileRef = getDocumentFileRef(doc);

  return (
    fileRef.includes(".docx") ||
    fileRef.includes(".doc") ||
    fileRef.includes("wordprocessingml") ||
    fileRef.includes("msword")
  );
};

const isLegacyDocDocument = (doc) => {
  const fileRef = getDocumentFileRef(doc);

  return fileRef.includes(".doc") && !fileRef.includes(".docx");
};

const normalizePageCount = (pages) => {
  const normalizedPages = Number(pages);
  return Number.isFinite(normalizedPages) && normalizedPages > 0
    ? normalizedPages
    : null;
};

const getKnownPageCount = (doc) => {
  return (
    normalizePageCount(doc?.totalPages) ||
    normalizePageCount(doc?.pageCount) ||
    normalizePageCount(doc?.pages) ||
    normalizePageCount(doc?.noOfPages) ||
    normalizePageCount(doc?.numberOfPages)
  );
};

const getFallbackPageCount = (doc) =>
  isPdfDocument(doc) || (isWordDocument(doc) && !isLegacyDocDocument(doc))
    ? 0
    : 1;

const getTrustedPageCount = (doc) => {
  return (
    normalizePageCount(doc?.pageCount) ||
    normalizePageCount(doc?.pages) ||
    normalizePageCount(doc?.noOfPages) ||
    normalizePageCount(doc?.numberOfPages) ||
    normalizePageCount(doc?.totalPageCount) ||
    normalizePageCount(doc?.page_count)
  );
};

const readCachedPageCount = (url) => {
  try {
    const value = window.sessionStorage.getItem(`pdf-page-count:${url}`);
    return normalizePageCount(value);
  } catch {
    return null;
  }
};

const writeCachedPageCount = (url, pages) => {
  try {
    window.sessionStorage.setItem(`pdf-page-count:${url}`, String(pages));
  } catch {
    // Storage can be unavailable in private browsing; the memory cache still works.
  }
};

const runNext = () => {
  if (activeReads >= MAX_CONCURRENT_PAGE_READS || queue.length === 0) return;

  const task = queue.shift();
  activeReads += 1;

  task()
    .catch(() => {})
    .finally(() => {
      activeReads -= 1;
      runNext();
    });
};

const enqueueRead = (task) =>
  new Promise((resolve, reject) => {
    queue.push(() => task().then(resolve).catch(reject));
    runNext();
  });

// Range-loads only the trailer and page tree on the shared PDF worker, instead
// of downloading (and parsing on the main thread) the whole file.
const readPdfPageCount = async (url) => {
  const loading = openPdfOnce(url);
  let timeoutId;

  try {
    const timeout = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        loading.destroy();
        reject(new Error("PDF page count timed out"));
      }, PAGE_COUNT_TIMEOUT_MS);
    });

    const pdf = await Promise.race([loading.promise, timeout]);
    return pdf?.numPages || 0;
  } finally {
    clearTimeout(timeoutId);
    loading.destroy();
  }
};

const decodeXmlText = (value = "") =>
  value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");

const getDocxText = (documentXml = "") => {
  const textParts = [];
  const textRegex = /<w:t[^>]*>([\s\S]*?)<\/w:t>/g;
  let match;

  while ((match = textRegex.exec(documentXml)) !== null) {
    textParts.push(decodeXmlText(match[1]));
  }

  return textParts.join(" ");
};

const estimateDocxPages = (documentXml = "") => {
  const text = getDocxText(documentXml);
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const characters = text.replace(/\s+/g, "").length;
  const explicitBreaks =
    (documentXml.match(/w:type="page"/g) || []).length +
    (documentXml.match(/<w:lastRenderedPageBreak\/?>/g) || []).length;

  const pagesFromBreaks = explicitBreaks ? explicitBreaks + 1 : 0;
  const pagesFromWords = words ? Math.ceil(words / 450) : 0;
  const pagesFromCharacters = characters ? Math.ceil(characters / 2600) : 0;

  return Math.max(pagesFromBreaks, pagesFromWords, pagesFromCharacters, 0);
};

const readDocxPageCount = async (url) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PAGE_COUNT_TIMEOUT_MS);

  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return 0;

    const buffer = await response.arrayBuffer();
    const zip = await JSZip.loadAsync(buffer);
    const appXml = await zip.file("docProps/app.xml")?.async("text");
    const documentXml = await zip.file("word/document.xml")?.async("text");
    const pagesFromMetadata = normalizePageCount(
      appXml?.match(/<Pages>(\d+)<\/Pages>/)?.[1],
    );
    const pagesFromContent = estimateDocxPages(documentXml);

    return Math.max(pagesFromMetadata || 0, pagesFromContent || 0);
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getPdfPageCount = (url, doc) => {
  if (!url) return Promise.resolve(1);
  if (
    !isPdfUrl(url) &&
    !isDocxUrl(url) &&
    !isDocUrl(url) &&
    !isPdfDocument(doc) &&
    !isWordDocument(doc)
  ) {
    return Promise.resolve(1);
  }
  if (pageCountCache[url]) return Promise.resolve(pageCountCache[url]);

  const storedPages = readCachedPageCount(url);
  if (storedPages) {
    pageCountCache[url] = storedPages;
    return Promise.resolve(storedPages);
  }

  if (pendingPageCountReads[url]) return pendingPageCountReads[url];

  pendingPageCountReads[url] = enqueueRead(async () => {
    try {
      const pages =
        isPdfUrl(url) || isPdfDocument(doc)
          ? await readPdfPageCount(url)
          : isLegacyDocDocument(doc) || isDocUrl(url)
            ? 1
            : await readDocxPageCount(url);
      if (pages) {
        pageCountCache[url] = pages;
        writeCachedPageCount(url, pages);
      }
      return pages;
    } catch (error) {
      console.error("Document page count read error:", error);
      return 0;
    } finally {
      delete pendingPageCountReads[url];
    }
  });

  return pendingPageCountReads[url];
};

export const hydratePdfPageCounts = async (docs = [], onProgress) => {
  const results = [...docs];
  const reads = results.map(async (doc, index) => {
    const url = getDocumentUrl(doc);
    const knownPages = isWordDocument(doc)
      ? getTrustedPageCount(doc) ||
        pageCountCache[url] ||
        readCachedPageCount(url)
      : getKnownPageCount(doc);

    if (knownPages) {
      results[index] = { ...doc, totalPages: knownPages };
      return;
    }

    const pages = url ? await getPdfPageCount(url, doc) : 1;
    const updatedDoc = { ...doc, totalPages: pages };
    results[index] = updatedDoc;
    if (onProgress) onProgress(updatedDoc, index);
  });

  await Promise.all(reads);
  return results;
};

export const applyKnownPdfPageCounts = (docs = []) =>
  docs.map((doc) => {
    const url = getDocumentUrl(doc);
    const pages =
      (isWordDocument(doc)
        ? getTrustedPageCount(doc)
        : getKnownPageCount(doc)) ||
      pageCountCache[url] ||
      readCachedPageCount(url) ||
      getFallbackPageCount(doc);

    return { ...doc, totalPages: pages };
  });
